import React, {FC, useEffect, useState} from 'react'
import {observer} from 'mobx-react'
import useStore from 'store'
import {useTranslation} from 'react-i18next'
import {Space} from '../../ui'
import { FeatureIds, SERVICE_DESK_FEATURE, SERVICE_DESK_URL_OPTIONS } from 'utils/types'
import { Switch } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import DeleteModuleButton from './DeleteModuleButton'
import {
    ModalRow,
    ModalText,
    ConfirmButtonSm,
    ButtonGroup,
    DialogHeaderText,
    ToggleGroup,
    ToggleRow,
    UrlInputWrapper,
    InputContainer,
    UrlInput,
    EmptyState
} from './styles'


export interface ServiceDeskFormState {
    isEnable: boolean
    link: string
}

interface ServiceDeskSectionProps {
    currentOrgId: number | undefined
    resetForm: boolean
}


const ServiceDeskSection: FC<ServiceDeskSectionProps> = ({ currentOrgId, resetForm }) => {
    const {modulesStore, orgs: orgsStore} = useStore()
    const {t} = useTranslation('communitySettings')
    const {t: tBudget} = useTranslation('budget')

    const [isFormChanged, setIsFormChanged] = useState<boolean>(false)
    const [module, setModule] = useState<any>(null)
    // Baseline link to diff against; updated after a successful save so the
    // button disables even though modulesStore.serviceDeskModule is stale.
    const [savedLink, setSavedLink] = useState<string>('')
    const [formState, setFormState] = useState<ServiceDeskFormState>({
        isEnable: false,
        link: ''
    })

    // The link/url lives on the service desk *module* (external_url), which is
    // separate from the enabled *feature*. It is only editable when the module
    // already exists (it carries the module_id the update endpoint needs) -
    // the module is created from the "add new module" dialog.
    const serviceDeskModule = modulesStore.serviceDeskModule
    const originalLink = serviceDeskModule?.external_url ?? ''
    const canEditLink = Boolean(serviceDeskModule?.module_id)

    // Nothing here can be configured until the module has been added to the
    // community from the "add new module" dialog.
    const moduleAdded = Boolean(serviceDeskModule?.module_id)

    useEffect(() => {
        const serviceDeskFeature = modulesStore.communityFeatures.find(el => el.featureName === SERVICE_DESK_FEATURE)
        if (serviceDeskFeature) {
            setModule(serviceDeskFeature)
            setFormState({ isEnable: serviceDeskFeature.enabled, link: originalLink })
        } else {
            orgsStore.updateCommunity(currentOrgId, [...modulesStore.communityFeatures, {
                communityId: currentOrgId,
                enabled: false,
                featureName: SERVICE_DESK_FEATURE,
                id: FeatureIds.SERVICE_DESK,
                status: 2,
            }]).then(async () => {
                await modulesStore.getCommunityModulesAndFeatures(currentOrgId)
                const newFeature = modulesStore.communityFeatures.find(el => el.featureName === SERVICE_DESK_FEATURE)
                if (newFeature) {
                    setModule(newFeature)
                    setFormState({ isEnable: newFeature.enabled, link: originalLink })
                }
            })
        }
    }, [currentOrgId])

    // Keep the link field in sync once the module prop finishes loading.
    useEffect(() => {
        setSavedLink(originalLink)
        setFormState(prev => ({ ...prev, link: originalLink }))
    }, [originalLink])

    useEffect(() => {
        if (resetForm) onResetForm()
    }, [resetForm])

    useEffect(() => {
        setIsFormChanged(
            formState.isEnable !== (module?.enabled ?? false) ||
            formState.link !== savedLink
        )
    }, [formState, module, savedLink])

    const Save = async () => {
        if (currentOrgId) {
            await orgsStore.updateCommunity(currentOrgId,
                modulesStore.communityFeatures.map(el => el.featureName === SERVICE_DESK_FEATURE ? {
                    communityId: el.communityId,
                    enabled: formState.isEnable,
                    featureName: el.featureName,
                    id: el.id,
                    status: formState.isEnable ? 1 : 2,
                } : el),)

            // Persist the link/url on the module itself if it changed.
            if (canEditLink && formState.link !== savedLink) {
                await modulesStore.updateModule({ ...serviceDeskModule, external_url: formState.link })
            }

            await modulesStore.getCommunityModulesAndFeatures(currentOrgId)

            const serviceDeskFeature = modulesStore.communityFeatures.find(el => el.featureName === SERVICE_DESK_FEATURE)
            if (serviceDeskFeature) {
                setModule(serviceDeskFeature)
            }
            const newLink = modulesStore.serviceDeskModule?.external_url ?? formState.link
            setSavedLink(newLink)
            setFormState({
                isEnable: serviceDeskFeature ? serviceDeskFeature.enabled : formState.isEnable,
                link: newLink
            })
        }
    }

    const onResetForm = () => setFormState({
        isEnable: false,
        link: savedLink
    })

    const ToggleEnable = (value: any) => setFormState({...formState, isEnable: value })

    return (
        <>
            <ModalRow>
               <DialogHeaderText>{tBudget`serviceDesk`}</DialogHeaderText>
               {moduleAdded &&
                 <ButtonGroup>
                   <DeleteModuleButton
                       module={serviceDeskModule}
                       moduleLabel={tBudget`serviceDesk`}
                       currentOrgId={currentOrgId}
                       onDeleted={onResetForm}
                   />
                   <ConfirmButtonSm disabled={!isFormChanged || module == null} onClick={Save} transparent>{t`save`}</ConfirmButtonSm>
                 </ButtonGroup>
               }
            </ModalRow>

            {!moduleAdded &&
                <EmptyState>
                    {t('moduleNotAdded', { module: tBudget`serviceDesk` })}
                </EmptyState>
            }

            {moduleAdded && module &&
                <ToggleGroup>
                    <ToggleRow>
                        <ModalText>{t`isEnable`}:</ModalText>
                        <Switch
                            color="primary"
                            value={formState.isEnable}
                            onChange={({target}) => {
                                ToggleEnable(target.checked)
                            }}
                            checked={formState.isEnable}
                        />
                    </ToggleRow>
                </ToggleGroup>
            }

            {canEditLink &&
            <UrlInputWrapper>
                <ModalText>{t`url`}:</ModalText>
                <Space/>
                <InputContainer>
                    <Autocomplete
                        freeSolo
                        options={SERVICE_DESK_URL_OPTIONS}
                        getOptionLabel={option => typeof option === 'string' ? option : option.text}
                        inputValue={formState.link}
                        onInputChange={(event, value) => setFormState({...formState, link: value})}
                        onChange={(event, value) => setFormState({
                            ...formState,
                            link: typeof value === 'string' ? value : value?.value ?? ''
                        })}
                        renderInput={params => (
                            <UrlInput
                                {...params}
                                placeholder={t`url`}
                            />
                        )}
                    />
                </InputContainer>
            </UrlInputWrapper>
            }

            <Space/>
            <Space/>
        </>
    )
}

export default observer(ServiceDeskSection)
